// src/pages/PharmaReports.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import _ from "lodash";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Select from "react-select";

import AppConst from "../apis/appconst";
import DelBox from "../comps/DelBox";
import NewOrder from "../models/NewOrder";

const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]),
    month = Number(m[2]) - 1,
    day = Number(m[3]);
  const hour = Number(m[4] ?? 0),
    minute = Number(m[5] ?? 0),
    second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}

const formatDateTime12 = (s) => {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
  ];
  const mon = months[dt.getMonth()];
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  return `${day}-${mon}-${dt.getFullYear()} ${pad(hr)}:${min} ${ampm}`;
};

function classifyMode(raw = "") {
  const s = String(raw || "").toLowerCase();
  if (s.includes("cash")) return "Cash";
  if (s.includes("card") || s.includes("debit") || s.includes("credit") || s.includes("swipe")) return "Card";
  if (s.includes("upi") || s.includes("online") || s.includes("gateway") || s.includes("net")) return "Online";
  return raw || "";
}

export default function PharmaReports() {
  const today = getTodayYMD();

  const [masterList, setMasterList] = useState([]);
  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [doctorsList, setDoctorsList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    search_key: "",
    from_date: today,
    to_date: today,
    from_bill_no: "",
    to_bill_no: "",
    selectedDoctor: null,
    selectedUser: "",
    selectedMedicines: [], // ✅ CHANGED: Multi-select array
    paymentType: "All",
    page_no: "none"
  });

  const [summary, setSummary] = useState({
    totalSaleAmount: 0,
    totalPatients: 0,
    todaysCollection: 0,
    collectionBy: { cash: 0, online: 0, card: 0 }
  });

  const [totals, setTotals] = useState({
    totalAmount: 0,
    discountAmount: 0,
    paidAmount: 0,
    dueAmount: 0,
    totalRows: 0,
    doctorCommission: 0,
    doctorCommissionPaid: 0,
    doctorCommissionDue: 0
  });

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#ced4da',
      '&:hover': { borderColor: '#86b7fe' }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      cursor: 'pointer'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e7f1ff',
      borderRadius: '4px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#0d6efd',
      fontWeight: 500,
      fontSize: '12px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#0d6efd',
      '&:hover': { backgroundColor: '#0d6efd', color: 'white' }
    })
  };

  // ✅ Extract unique medicines for react-select
  const medicinesList = useMemo(() => {
    const medicinesMap = new Map();
    masterList.forEach(bill => {
      const items = Array.isArray(bill.items) ? bill.items : [];
      items.forEach(item => {
        const medicineName = item.med_bill_item_medicine || "";
        if (medicineName && !medicinesMap.has(medicineName)) {
          medicinesMap.set(medicineName, {
            value: medicineName,
            label: medicineName,
            batch: item.med_bill_item_batch || "",
          });
        }
      });
    });
    return Array.from(medicinesMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [masterList]);

  const doctorsOptions = useMemo(() => {
    return doctorsList.map(d => ({
      value: d.user_id,
      label: d.fname || d.name || d.username || `Doctor #${d.user_id}`
    }));
  }, [doctorsList]);

  // ✅ NEW: Medicine Sale Summary - computed from filtered bills
  const medicineSaleSummary = useMemo(() => {
    const selectedNames = (formData.selectedMedicines || []).map(m => m.value);
    if (selectedNames.length === 0) return null;

    // Per-medicine stats
    const medStats = {};
    selectedNames.forEach(name => {
      medStats[name] = {
        name,
        totalPackageQty: 0,
        totalLooseQty: 0,
        totalQty: 0,
        totalAmount: 0,
        patients: new Map(), // uhid/name -> { name, uhid, phone, qty, loose, pkg, bills[] }
        batches: new Set(),
        billCount: 0,
      };
    });

    // Scan all filtered bills
    masterList.forEach(bill => {
      const items = Array.isArray(bill.items) ? bill.items : [];
      const billDate = bill.med_bill_datetime || bill.med_bill_date || "";
      const patientName = bill.med_bill_patient_number || "Walk-in";
      const uhid = bill.med_bill_ipd_number || "-";
      const phone = bill.med_bill_phone_number || bill.med_bill_patient_mobile || "-";
      const billNo = bill.med_bill_id || "";

      // Date filter
      if (formData.from_date) {
        const from = new Date(`${formData.from_date}T00:00`);
        const d = parseServerDateToLocal(billDate);
        if (d && d < from) return;
      }
      if (formData.to_date) {
        const to = new Date(`${formData.to_date}T23:59:59`);
        const d = parseServerDateToLocal(billDate);
        if (d && d > to) return;
      }

      items.forEach(item => {
        const medName = item.med_bill_item_medicine || "";
        if (!selectedNames.includes(medName)) return;

        const stat = medStats[medName];
        const qty = parseInt(item.med_bill_item_qty || 0);
        const isLoose = parseInt(item.med_bill_item_is_loose || 0) === 1;
        const mrp = parseFloat(item.med_bill_item_mrp || 0);
        const discount = parseFloat(item.med_bill_item_discount || 0);
        const lineTotal = (mrp - discount) * qty;

        if (isLoose) {
          stat.totalLooseQty += qty;
        } else {
          stat.totalPackageQty += qty;
        }
        stat.totalQty += qty;
        stat.totalAmount += lineTotal;
        stat.billCount += 1;

        if (item.med_bill_item_batch) {
          stat.batches.add(item.med_bill_item_batch);
        }

        // Track patient
        const patientKey = uhid !== "-" ? uhid : patientName;
        if (!stat.patients.has(patientKey)) {
          stat.patients.set(patientKey, {
            name: patientName,
            uhid,
            phone,
            packageQty: 0,
            looseQty: 0,
            totalQty: 0,
            amount: 0,
            bills: [],
            dates: []
          });
        }
        const p = stat.patients.get(patientKey);
        if (isLoose) p.looseQty += qty; else p.packageQty += qty;
        p.totalQty += qty;
        p.amount += lineTotal;
        if (!p.bills.includes(String(billNo))) p.bills.push(String(billNo));
        const dateStr = billDate ? formatDateTime12(billDate).split(" ")[0] : "";
        if (dateStr && !p.dates.includes(dateStr)) p.dates.push(dateStr);
      });
    });

    // Convert maps/sets
    const results = selectedNames.map(name => {
      const s = medStats[name];
      return {
        ...s,
        patients: Array.from(s.patients.values()),
        batches: Array.from(s.batches),
        uniquePatients: s.patients.size
      };
    });

    // Grand totals
    const grandTotalQty = results.reduce((sum, r) => sum + r.totalQty, 0);
    const grandPackageQty = results.reduce((sum, r) => sum + r.totalPackageQty, 0);
    const grandLooseQty = results.reduce((sum, r) => sum + r.totalLooseQty, 0);
    const grandAmount = results.reduce((sum, r) => sum + r.totalAmount, 0);
    const grandBills = results.reduce((sum, r) => sum + r.billCount, 0);

    // All unique patients across medicines
    const allPatientsMap = new Map();
    results.forEach(r => {
      r.patients.forEach(p => {
        const key = p.uhid !== "-" ? p.uhid : p.name;
        if (!allPatientsMap.has(key)) {
          allPatientsMap.set(key, { ...p, medicines: [r.name] });
        } else {
          const existing = allPatientsMap.get(key);
          existing.totalQty += p.totalQty;
          existing.packageQty += p.packageQty;
          existing.looseQty += p.looseQty;
          existing.amount += p.amount;
          if (!existing.medicines.includes(r.name)) existing.medicines.push(r.name);
          p.bills.forEach(b => { if (!existing.bills.includes(b)) existing.bills.push(b); });
          p.dates.forEach(d => { if (!existing.dates.includes(d)) existing.dates.push(d); });
        }
      });
    });

    return {
      medicines: results,
      grandTotalQty,
      grandPackageQty,
      grandLooseQty,
      grandAmount,
      grandBills,
      allPatients: Array.from(allPatientsMap.values()),
      totalUniquePatients: allPatientsMap.size,
      isMulti: selectedNames.length > 1
    };
  }, [masterList, formData.selectedMedicines, formData.from_date, formData.to_date]);

  // Fetch payment transactions
  const fetchPaymentTransactions = useCallback(async (currentForm = formData) => {
    try {
      const fd = new FormData();
      if (currentForm.from_date) fd.append("from_date", currentForm.from_date);
      if (currentForm.to_date) fd.append("to_date", currentForm.to_date);
      if (currentForm.selectedUser) fd.append("user_id", currentForm.selectedUser);
      if (currentForm.from_bill_no) fd.append("from_bill_no", currentForm.from_bill_no);
      if (currentForm.to_bill_no) fd.append("to_bill_no", currentForm.to_bill_no);

      const res = await axios.post(
        AppConst.BASE_URL + AppConst.getMedPaymentTransactionsUrl, fd
      );

      if (res?.data?.status && Array.isArray(res.data.records)) {
        setPaymentTransactions(res.data.records);
        if (res.data.totals) {
          setSummary(prev => ({
            ...prev,
            collectionBy: {
              cash: res.data.totals.cash || 0,
              card: res.data.totals.card || 0,
              online: res.data.totals.online || 0
            }
          }));
        }
      } else {
        setPaymentTransactions([]);
      }
    } catch (err) {
      console.error("fetchPaymentTransactions error", err);
      setPaymentTransactions([]);
    }
  }, [formData]);

  const exportPaymentTransactionsPDF = () => {
    if (!filteredPayments || filteredPayments.length === 0) {
      toast.info("No payment transactions to export");
      return;
    }

    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(14);
    doc.text("Pharmacy Payment Transactions", pageWidth / 2, 12, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Date Range: ${formData.from_date || "-"} to ${formData.to_date || "-"}`, pageWidth / 2, 18, { align: "center" });
    doc.text(`Cash: ${filteredPaymentSummary.cash.toFixed(2)}`, 14, 26);
    doc.text(`Card: ${filteredPaymentSummary.card.toFixed(2)}`, 80, 26);
    doc.text(`Online: ${filteredPaymentSummary.online.toFixed(2)}`, 150, 26);

    autoTable(doc, {
      startY: 32,
      head: [["#", "Bill No", "UHID", "Patient", "Bill Total", "Paid", "Due", "Txn Amount", "Mode", "Date & Time"]],
      body: filteredPayments.map((p, i) => [
        i + 1,
        p.med_bill_id || "-",
        p.med_bill_ipd_number || "-",
        p.med_bill_patient_number || "-",
        Number(p.med_bill_final_amt || 0).toFixed(2),
        Number(p.med_bill_paid || 0).toFixed(2),
        Number(p.med_bill_due || 0).toFixed(2),
        Number(p.payment_amount || 0).toFixed(2),
        classifyMode(p.payment_mode),
        formatDateTime12(p.payment_datetime)
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [64, 81, 137], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 250] }
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.text(`Total Transactions: ${filteredPayments.length}`, 14, finalY);
    doc.text(`Grand Total: ${(filteredPaymentSummary.cash + filteredPaymentSummary.card + filteredPaymentSummary.online).toFixed(2)}`, pageWidth - 14, finalY, { align: "right" });
    doc.save(`Pharmacy_Payment_Transactions_${formData.from_date}_to_${formData.to_date}.pdf`);
  };

  // ✅ NEW: Export Medicine Sale Summary PDF
  const exportMedicineSummaryPDF = () => {
    if (!medicineSaleSummary) return;

    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 12;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Medicine Sale Summary Report", pageWidth / 2, y, { align: "center" });
    y += 8;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Date: ${formData.from_date} to ${formData.to_date}`, pageWidth / 2, y, { align: "center" });
    y += 6;

    const selectedNames = medicineSaleSummary.medicines.map(m => m.name).join(", ");
    doc.setFontSize(9);
    doc.text(`Medicines: ${selectedNames}`, 14, y);
    y += 8;

    // Summary row
    doc.setFontSize(9);
    doc.text(`Total Pkg Sold: ${medicineSaleSummary.grandPackageQty}`, 14, y);
    doc.text(`Total Loose Sold: ${medicineSaleSummary.grandLooseQty}`, 80, y);
    doc.text(`Total Qty: ${medicineSaleSummary.grandTotalQty}`, 150, y);
    doc.text(`Total Amount: Rs ${medicineSaleSummary.grandAmount.toFixed(2)}`, 210, y);
    y += 8;

    // Patient details table
    autoTable(doc, {
      startY: y,
      head: [["#", "Patient Name", "UHID", "Phone", "Bill No(s)", "Pkg Qty", "Loose Qty", "Total Qty", "Amount"]],
      body: medicineSaleSummary.allPatients.map((p, i) => [
        i + 1,
        p.name,
        p.uhid,
        p.phone,
        p.bills.join(", "),
        p.packageQty,
        p.looseQty,
        p.totalQty,
        `Rs ${p.amount.toFixed(2)}`
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 247, 250] }
    });

    doc.save(`Medicine_Sale_Summary_${formData.from_date}_to_${formData.to_date}.pdf`);
    toast.success("Medicine summary PDF exported!");
  };

  // ---------- API calls ----------
  const initData = useCallback(async (currentForm = formData) => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("page_no", currentForm.page_no ?? "none");
      fd.append("search_key", currentForm.search_key ?? "");
      fd.append("from_date", currentForm.from_date ?? "");
      fd.append("to_date", currentForm.to_date ?? "");
      fd.append("med_bill_type", "all");
      if (currentForm.selectedDoctor?.value) fd.append("doctor_id", currentForm.selectedDoctor.value);
      if (currentForm.from_bill_no) fd.append("from_bill_no", currentForm.from_bill_no);
      if (currentForm.to_bill_no) fd.append("to_bill_no", currentForm.to_bill_no);

      const res = await axios.post(AppConst.BASE_URL + AppConst.getMedBillListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setMasterList(res.data.records);
      } else {
        setMasterList([]);
      }
      await fetchPaymentTransactions(currentForm);
    } catch (err) {
      console.error("initData error", err);
      setMasterList([]);
    } finally {
      setLoading(false);
    }
  }, [formData, fetchPaymentTransactions]);

  const fetchDoctors = async () => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("ldata") || "{}");
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("user_cre_by", stored.user_id ?? "");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setDoctorsList(res.data.records);
      }
    } catch (e) {
      console.error("fetchDoctors error", e);
    }
  };

  useEffect(() => {
    initData(formData);
    fetchDoctors();
  }, []);

  useEffect(() => {
    const map = {};
    masterList.forEach(r => {
      const uid = r.med_bill_user ?? r.user_id ?? r.created_by ?? null;
      const uname = r.user_display || r.last_used || r.fname || `User#${uid}`;
      if (uid) map[String(uid)] = uname;
    });
    setUsers(Object.entries(map).map(([user_id, name]) => ({ user_id, name })));
  }, [masterList]);

  // ---------- Filtering ----------
  const filteredRecords = useMemo(() => {
    let data = [...masterList];
    const hasBillNoFilter = formData.from_bill_no || formData.to_bill_no;

    if (!hasBillNoFilter) {
      if (formData.from_date) {
        const from = new Date(`${formData.from_date}T00:00`);
        data = data.filter(r => {
          const d = parseServerDateToLocal(r.med_bill_datetime ?? r.med_bill_date ?? null);
          return d ? d >= from : true;
        });
      }
      if (formData.to_date) {
        const to = new Date(`${formData.to_date}T23:59:59`);
        data = data.filter(r => {
          const d = parseServerDateToLocal(r.med_bill_datetime ?? r.med_bill_date ?? null);
          return d ? d <= to : true;
        });
      }
    }

    if (formData.from_bill_no) data = data.filter(r => Number(r.med_bill_id || 0) >= Number(formData.from_bill_no));
    if (formData.to_bill_no) data = data.filter(r => Number(r.med_bill_id || 0) <= Number(formData.to_bill_no));

    if (formData.selectedDoctor?.value) {
      const sel = String(formData.selectedDoctor.value).toLowerCase();
      data = data.filter(r => {
        const idMatch = String(r.med_bill_ref_doctor ?? r.med_bill_doctor ?? "").toLowerCase() === sel;
        const nameMatch = ((r.med_bill_ref_doctor_name ?? r.med_bill_doctor_name ?? "") + "").toLowerCase().includes(sel);
        return idMatch || nameMatch;
      });
    }

    if (formData.selectedUser) {
      data = data.filter(r => String(r.med_bill_user || "").toLowerCase() === String(formData.selectedUser).toLowerCase());
    }

    // ✅ MULTI medicine filter
    const selectedMeds = (formData.selectedMedicines || []).map(m => m.value);
    if (selectedMeds.length > 0) {
      data = data.filter(r => {
        const items = Array.isArray(r.items) ? r.items : [];
        return items.some(item => selectedMeds.includes(item.med_bill_item_medicine));
      });
    }

    if (formData.paymentType && formData.paymentType !== "All") {
      const wanted = formData.paymentType.toLowerCase();
      data = data.filter(r => {
        const modeRaw = (r.med_bill_payment_mode || r.med_bill_paymentmode || r.med_bill_mode_of_payment || "");
        return classifyMode(modeRaw).toLowerCase() === wanted;
      });
    }

    if (formData.search_key && formData.search_key.trim() !== "") {
      const sk = formData.search_key.trim().toLowerCase();
      data = data.filter(r => {
        const uhid = (r.med_bill_ipd_number || "").toString().toLowerCase();
        const name = (r.med_bill_patient_number || "").toString().toLowerCase();
        const phone = (r.med_bill_phone_number || r.med_bill_patient_mobile || "").toString().toLowerCase();
        return uhid.includes(sk) || name.includes(sk) || phone.includes(sk);
      });
    }

    return data;
  }, [masterList, formData]);

  const filteredPayments = useMemo(() => {
    let data = [...paymentTransactions];
    const hasBillNoFilter = formData.from_bill_no || formData.to_bill_no;

    if (!hasBillNoFilter) {
      if (formData.from_date) {
        const from = new Date(`${formData.from_date}T00:00`);
        data = data.filter(p => { const d = parseServerDateToLocal(p.payment_datetime); return d ? d >= from : true; });
      }
      if (formData.to_date) {
        const to = new Date(`${formData.to_date}T23:59:59`);
        data = data.filter(p => { const d = parseServerDateToLocal(p.payment_datetime); return d ? d <= to : true; });
      }
    }
    if (formData.from_bill_no) data = data.filter(p => Number(p.med_bill_id || 0) >= Number(formData.from_bill_no));
    if (formData.to_bill_no) data = data.filter(p => Number(p.med_bill_id || 0) <= Number(formData.to_bill_no));
    if (formData.selectedUser) data = data.filter(p => String(p.user_id || "").toLowerCase() === String(formData.selectedUser).toLowerCase());
    if (formData.paymentType && formData.paymentType !== "All") {
      const wanted = formData.paymentType.toLowerCase();
      data = data.filter(p => classifyMode(p.payment_mode).toLowerCase() === wanted);
    }
    if (formData.search_key && formData.search_key.trim() !== "") {
      const sk = formData.search_key.trim().toLowerCase();
      data = data.filter(p => {
        return (p.med_bill_ipd_number || "").toLowerCase().includes(sk) ||
               (p.med_bill_patient_number || "").toLowerCase().includes(sk) ||
               (p.med_bill_phone_number || "").toLowerCase().includes(sk) ||
               (p.med_bill_id || "").toString().toLowerCase().includes(sk);
      });
    }
    return data;
  }, [paymentTransactions, formData]);

  const filteredPaymentSummary = useMemo(() => {
    let cash = 0, card = 0, online = 0;
    filteredPayments.forEach(p => {
      const amt = parseFloat(p.payment_amount || 0);
      const mode = classifyMode(p.payment_mode || "").toLowerCase();
      if (mode === "cash") cash += amt;
      else if (mode === "card") card += amt;
      else if (mode === "online") online += amt;
    });
    return { cash, card, online };
  }, [filteredPayments]);

  useEffect(() => {
    if (!filteredRecords || filteredRecords.length === 0) {
      setSummary(prev => ({ ...prev, totalSaleAmount: 0, totalPatients: 0, todaysCollection: 0 }));
      setTotals({ totalAmount: 0, discountAmount: 0, paidAmount: 0, dueAmount: 0, totalRows: 0, doctorCommission: 0, doctorCommissionPaid: 0, doctorCommissionDue: 0 });
      return;
    }
    let totalSale = 0, totalDisc = 0, totalPaid = 0, totalDue = 0, totalComm = 0, todaysColl = 0;

    filteredRecords.forEach(item => {
      const finalAmt = parseFloat(item.med_bill_final_amt) || 0;
      const discount = parseFloat(item.med_bill_discount_amt || item.med_bill_discount || item.med_bill_disc) || 0;
      const paid = parseFloat(item.med_bill_paid) || 0;
      const due = parseFloat(item.med_bill_due) || 0;
      const commission = parseFloat(item.doctor_commission || item.med_bill_doctor_commission) || 0;
      totalSale += finalAmt; totalDisc += discount; totalPaid += paid; totalDue += due; totalComm += commission;
      const raw = item.med_bill_datetime ?? item.med_bill_date ?? "";
      if (String(raw).split("T")[0] === today) todaysColl += paid;
    });

    setSummary(prev => ({ ...prev, totalSaleAmount: totalSale, totalPatients: filteredRecords.length, todaysCollection: todaysColl }));
    setTotals({ totalAmount: totalSale, discountAmount: totalDisc, paidAmount: totalPaid, dueAmount: totalDue, totalRows: filteredRecords.length, doctorCommission: totalComm, doctorCommissionPaid: 0, doctorCommissionDue: 0 });
  }, [filteredRecords, today]);

  // ---------- Handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (["from_date", "to_date", "from_bill_no", "to_bill_no"].includes(name)) {
      setTimeout(() => initData({ ...formData, [name]: value }), 50);
    }
  };

  const debouncedInit = useCallback(_.debounce(initData, 500), [initData]);
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    debouncedInit({ ...formData, [name]: value });
  };

  const handleDoctorChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, selectedDoctor: selectedOption }));
    initData({ ...formData, selectedDoctor: selectedOption });
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
    fetchPaymentTransactions(newFormData);
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ✅ MULTI medicine handler
  const handleMedicineChange = (selectedOptions) => {
    setFormData(prev => ({ ...prev, selectedMedicines: selectedOptions || [] }));
  };

  const viewMedicines = (row) => {
    const items = Array.isArray(row.items) ? row.items : [];
    if (items.length === 0) {
      Swal.fire({ icon: "info", title: "No items", text: "No medicines found for this bill.", confirmButtonColor: "#3085d6" });
      return;
    }

    const selectedNames = (formData.selectedMedicines || []).map(m => m.value);

    const html = `
      <div style="max-height:450px; overflow:auto; text-align:left;">
        <table class="table table-bordered table-hover" style="width:100%; font-size:13px; margin-bottom: 0;">
          <thead style="position: sticky; top: 0; background: #f8f9fa; z-index: 1;">
            <tr style="background: #e9ecef;">
              <th style="padding: 10px; text-align: center;">#</th>
              <th style="padding: 10px;">Medicine</th>
              <th style="padding: 10px;">Batch</th>
              <th style="padding: 10px; text-align: center;">Qty</th>
              <th style="padding: 10px; text-align: center;">Type</th>
              <th style="padding: 10px;">EXP</th>
              <th style="padding: 10px; text-align: right;">MRP</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((m, i) => {
              const isSelected = selectedNames.length > 0 && selectedNames.includes(m.med_bill_item_medicine);
              const rowStyle = isSelected
                ? 'background: linear-gradient(90deg, #e7f1ff 0%, #ffffff 100%); border-left: 4px solid #0d6efd;'
                : '';
              return `
              <tr style="${rowStyle}">
                <td style="padding: 8px; text-align: center;">${isSelected ? '<i class="ri-checkbox-circle-fill" style="color: #0d6efd; font-size: 18px;"></i>' : i+1}</td>
                <td style="padding: 8px; ${isSelected ? 'font-weight: bold; color: #0d6efd;' : ''}">
                  ${m.med_bill_item_medicine || "-"}
                  ${isSelected ? ' <span class="badge bg-primary" style="font-size: 9px;">FILTERED</span>' : ''}
                </td>
                <td style="padding: 8px;">${m.med_bill_item_batch || "-"}</td>
                <td style="padding: 8px; text-align: center; font-weight: bold;">${m.med_bill_item_qty || "0"}</td>
                <td style="padding: 8px; text-align: center;">
                  ${parseInt(m.med_bill_item_is_loose || 0) === 1
                    ? '<span class="badge bg-warning text-dark">Loose</span>'
                    : '<span class="badge bg-success">Package</span>'}
                </td>
                <td style="padding: 8px;">${m.med_bill_item_exp || "-"}</td>
                <td style="padding: 8px; text-align: right;">₹${m.med_bill_item_mrp || "0.00"}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>`;

    Swal.fire({
      title: `<div style="display: flex; align-items: center; gap: 10px;">
                <i class="ri-medicine-bottle-line" style="font-size: 24px; color: #3085d6;"></i>
                <span>Medicines — Bill #${row.med_bill_id || ""}</span>
              </div>`,
      html,
      width: 950,
      showCloseButton: true,
      confirmButtonText: "Close",
      confirmButtonColor: "#3085d6"
    });
  };

  const handleViewMedicines = (row, e) => { e.stopPropagation(); viewMedicines(row); };
  const handlePrint = (row, e) => { e.stopPropagation(); Swal.fire({ icon: "info", text: "Print handler called." }); };

  // ---------- DataTable columns ----------
  const billColumns = [
    { name: "SL", selector: (r,i) => i+1, width: "60px", center: true },
    { name: "Bill No", selector: r => r.med_bill_id || "", sortable: true, width: "100px" },
    { name: "UHID", selector: r => r.med_bill_ipd_number || "", sortable: true, width: "140px" },
    { name: "Patient", selector: r => r.med_bill_patient_number || "", sortable: true, wrap: true },
    {
      name: "View",
      width: "100px",
      center: true,
      cell: (row) => (
        <button onClick={(e) => handleViewMedicines(row, e)} className="btn btn-sm btn-outline-primary" title="View medicines">
          <i className="ri-eye-line"></i> View
        </button>
      ),
      ignoreRowClick: true, allowOverflow: true, button: true
    },
    { name: "Referral", selector: r => r.med_bill_ref_doctor_name || r.med_bill_ref_doctor || "", sortable: true, width: "180px" },
    { name: "Payment Type", selector: r => classifyMode(r.med_bill_payment_mode || r.med_bill_paymentmode || r.med_bill_mode_of_payment || ""), sortable: true, width: "140px" },
    { name: "Total", selector: r => Number(r.med_bill_final_amt || 0).toFixed(2), sortable: true, right: true, width: "110px" },
    { name: "Paid", selector: r => Number(r.med_bill_paid || 0).toFixed(2), right: true, width: "110px" },
    { name: "Due", selector: r => Number(r.med_bill_due || 0).toFixed(2), right: true, width: "110px" },
    { name: "Date", selector: r => r.med_bill_datetime || r.med_bill_date || "", cell: r => formatDateTime12(r.med_bill_datetime || r.med_bill_date), sortable: true, width: "180px" },
    { name: "User", selector: r => r.user_display || r.med_bill_user || "", width: "160px" },
    {
      name: "Actions",
      width: "100px",
      center: true,
      cell: (row) => (
        <button onClick={(e) => handlePrint(row, e)} className="btn btn-sm btn-outline-info" title="Print bill">
          <i className="ri-printer-line"></i> Print
        </button>
      ),
      ignoreRowClick: true, allowOverflow: true, button: true
    }
  ];

  const paymentColumns = [
    { name: "#", selector: (r,i) => i+1, width: "60px", center: true },
    { name: "Bill No", selector: r => r.med_bill_id || "-", sortable: true, width: "100px" },
    { name: "UHID", selector: r => r.med_bill_ipd_number || "-", width: "130px" },
    { name: "Patient", selector: r => r.med_bill_patient_number || "-", wrap: true },
    { name: "Bill Total", selector: r => Number(r.med_bill_final_amt || 0).toFixed(2), right: true, width: "120px" },
    { name: "Paid", selector: r => Number(r.med_bill_paid || 0).toFixed(2), right: true, width: "110px" },
    { name: "Due", selector: r => Number(r.med_bill_due || 0).toFixed(2), right: true, width: "110px" },
    { name: "Txn Amount", selector: r => Number(r.payment_amount || 0).toFixed(2), right: true, width: "120px" },
    {
      name: "Mode",
      selector: r => classifyMode(r.payment_mode),
      width: "120px",
      cell: r => {
        const mode = classifyMode(r.payment_mode);
        const color = mode === "Cash" ? "success" : mode === "Card" ? "primary" : "warning";
        return <span className={`badge bg-${color}`}>{mode}</span>;
      }
    },
    { name: "Date/Time", selector: r => r.payment_datetime, cell: r => formatDateTime12(r.payment_datetime), width: "180px", sortable: true }
  ];

  const customStyles = {
    headCells: { style: { backgroundColor: "#f1f5fb", color: "#405189", fontSize: "14px", fontWeight: 700, cursor: 'default' } },
    rows: { style: { minHeight: "56px", cursor: 'pointer' }, highlightOnHoverStyle: { backgroundColor: '#f8f9fa', borderBottomColor: '#dee2e6', outline: '1px solid #dee2e6' } },
    cells: { style: { paddingLeft: "12px", paddingRight: "12px" } }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <style>{`
          .rdt_TableRow:hover { box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: box-shadow 0.2s ease; }
          .medicine-popup .swal2-html-container { padding: 0 !important; margin: 0 !important; }
        `}</style>

        {/* Summary Cards */}
        <div className="row mb-3">
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Patients</p><h4>{summary.totalPatients}</h4></div></div>
          <div className="col-md-3">
            <div className="card p-3">
              <p className="mb-1 text-muted">Total Amount (Transactions)</p>
              <h4>₹{(filteredPaymentSummary.cash + filteredPaymentSummary.card + filteredPaymentSummary.online).toFixed(2)}</h4>
            </div>
          </div>
          <div className="col-md-3"><div className="card p-3"><p className="mb-1 text-muted">Total Due</p><h4>₹{totals.dueAmount.toFixed(2)}</h4></div></div>
        </div>

        {/* Payment Mode Cards */}
        <div className="row mb-3">
          <div className="col-md-4"><div className="card p-3 bg-light"><p className="mb-1 text-muted">Cash Collection</p><h4 className="text-success">₹{filteredPaymentSummary.cash.toFixed(2)}</h4></div></div>
          <div className="col-md-4"><div className="card p-3 bg-light"><p className="mb-1 text-muted">Card Collection</p><h4 className="text-primary">₹{filteredPaymentSummary.card.toFixed(2)}</h4></div></div>
          <div className="col-md-4"><div className="card p-3 bg-light"><p className="mb-1 text-muted">Online Collection</p><h4 className="text-warning">₹{filteredPaymentSummary.online.toFixed(2)}</h4></div></div>
        </div>

        {/* Filter Panel */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search</label>
                <input name="search_key" value={formData.search_key} onChange={handleSearchChange} className="form-control" placeholder="UHID / Patient / Mobile / Bill No" />
              </div>
              <div className="col-md-2"><label>From Date</label><input type="date" name="from_date" value={formData.from_date} onChange={handleChange} className="form-control" /></div>
              <div className="col-md-2"><label>To Date</label><input type="date" name="to_date" value={formData.to_date} onChange={handleChange} className="form-control" /></div>
              <div className="col-md-2"><label>From Bill No</label><input type="number" name="from_bill_no" value={formData.from_bill_no} onChange={handleChange} className="form-control" placeholder="Start bill #" /></div>
              <div className="col-md-2"><label>To Bill No</label><input type="number" name="to_bill_no" value={formData.to_bill_no} onChange={handleChange} className="form-control" placeholder="End bill #" /></div>
            </div>

            <div className="row g-2 mt-3">
              <div className="col-md-3">
                <label><i className="ri-stethoscope-line"></i> Doctor</label>
                <Select value={formData.selectedDoctor} onChange={handleDoctorChange} options={doctorsOptions} styles={selectStyles} isClearable placeholder="Select Doctor..." noOptionsMessage={() => "No doctors found"} />
              </div>

              {/* ✅ MULTI-SELECT Medicine */}
              <div className="col-md-3">
                <label><i className="ri-medicine-bottle-line"></i> Medicine {formData.selectedMedicines?.length > 0 && <span className="badge bg-primary ms-1">{formData.selectedMedicines.length} selected</span>}</label>
                <Select
                  value={formData.selectedMedicines}
                  onChange={handleMedicineChange}
                  options={medicinesList}
                  styles={selectStyles}
                  isMulti
                  isClearable
                  placeholder="Select Medicine(s)..."
                  noOptionsMessage={() => "No medicines found"}
                  closeMenuOnSelect={false}
                />
              </div>

              <div className="col-md-2">
                <label>User</label>
                <select name="selectedUser" value={formData.selectedUser} onChange={handleUserChange} className="form-select">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <label>Payment</label>
                <select name="paymentType" value={formData.paymentType} onChange={handlePaymentChange} className="form-select">
                  <option value="All">All</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Online">Online</option>
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <div className="d-flex gap-2">
                  <button className="btn btn-success" onClick={() => initData(formData)}>Search</button>
                  <button className="btn btn-info" onClick={() => {
                    const cleared = { search_key: "", from_date: today, to_date: today, from_bill_no: "", to_bill_no: "", selectedDoctor: null, selectedUser: "", selectedMedicines: [], paymentType: "All", page_no: "none" };
                    setFormData(cleared);
                    initData(cleared);
                  }}>Clear</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ NEW: Medicine Sale Summary Panel */}
        {medicineSaleSummary && (formData.selectedMedicines || []).length > 0 && (
          <div className="card mb-3 border-primary">
            <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#405189', color: '#fff' }}>
              <h5 className="mb-0">
                <i className="ri-bar-chart-box-line me-2"></i>
                Medicine Sale Summary
                {medicineSaleSummary.isMulti && (
                  <span className="badge bg-warning text-dark ms-2">{formData.selectedMedicines.length} Medicines</span>
                )}
              </h5>
              <button className="btn btn-sm btn-light" onClick={exportMedicineSummaryPDF}>
                <i className="ri-file-pdf-line me-1"></i> Export PDF
              </button>
            </div>
            <div className="card-body">

              {/* Per-medicine summary cards */}
              {medicineSaleSummary.medicines.map((med, idx) => (
                <div key={idx} className="mb-3">
                  {medicineSaleSummary.isMulti && (
                    <h6 className="text-primary mb-2">
                      <i className="ri-capsule-line me-1"></i> {med.name}
                      <span className="badge bg-secondary ms-2">{med.batches.length} batch(es)</span>
                    </h6>
                  )}
                  <div className="row mb-2">
                    <div className="col-md-2">
                      <div className="card border-success mb-0">
                        <div className="card-body text-center py-2">
                          <small className="text-muted d-block">Package Sold</small>
                          <h4 className="text-success mb-0">{med.totalPackageQty}</h4>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card border-info mb-0">
                        <div className="card-body text-center py-2">
                          <small className="text-muted d-block">Loose Sold</small>
                          <h4 className="text-info mb-0">{med.totalLooseQty}</h4>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card border-primary mb-0">
                        <div className="card-body text-center py-2">
                          <small className="text-muted d-block">Total Qty Sold</small>
                          <h4 className="text-primary mb-0">{med.totalQty}</h4>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card border-warning mb-0">
                        <div className="card-body text-center py-2">
                          <small className="text-muted d-block">Total Amount</small>
                          <h4 className="text-warning mb-0">₹{med.totalAmount.toFixed(2)}</h4>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card border-secondary mb-0">
                        <div className="card-body text-center py-2">
                          <small className="text-muted d-block">Patients</small>
                          <h4 className="text-secondary mb-0">{med.uniquePatients}</h4>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card border-dark mb-0">
                        <div className="card-body text-center py-2">
                          <small className="text-muted d-block">Sale Entries</small>
                          <h4 className="mb-0">{med.billCount}</h4>
                        </div>
                      </div>
                    </div>
                  </div>

                  {medicineSaleSummary.isMulti && idx < medicineSaleSummary.medicines.length - 1 && (
                    <hr className="my-2" />
                  )}
                </div>
              ))}

              {/* Grand totals for multi-select */}
              {medicineSaleSummary.isMulti && (
                <div className="row mb-3 mt-2">
                  <div className="col-12">
                    <div className="alert alert-primary d-flex justify-content-between align-items-center mb-0 py-2">
                      <strong><i className="ri-calculator-line me-1"></i> Grand Total ({formData.selectedMedicines.length} medicines):</strong>
                      <div className="d-flex gap-4">
                        <span>Pkg: <strong>{medicineSaleSummary.grandPackageQty}</strong></span>
                        <span>Loose: <strong>{medicineSaleSummary.grandLooseQty}</strong></span>
                        <span>Total Qty: <strong>{medicineSaleSummary.grandTotalQty}</strong></span>
                        <span>Amount: <strong>₹{medicineSaleSummary.grandAmount.toFixed(2)}</strong></span>
                        <span>Patients: <strong>{medicineSaleSummary.totalUniquePatients}</strong></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Patient-wise breakdown table */}
              <h6 className="mt-3 mb-2">
                <i className="ri-user-line me-1"></i>
                Patient-wise Breakdown
                <span className="badge bg-secondary ms-2">{medicineSaleSummary.allPatients.length} patient(s)</span>
              </h6>
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-bordered table-hover table-sm align-middle mb-0">
                  <thead style={{ backgroundColor: '#f1f5fb', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '40px' }}>Sl#</th>
                      <th>Patient Name</th>
                      <th>UHID</th>
                      <th>Phone</th>
                      <th>Bill No(s)</th>
                      {medicineSaleSummary.isMulti && <th>Medicine(s)</th>}
                      <th className="text-center">Pkg Qty</th>
                      <th className="text-center">Loose Qty</th>
                      <th className="text-center">Total Qty</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicineSaleSummary.allPatients.map((p, idx) => (
                      <tr key={idx}>
                        <td className="text-center">{idx + 1}</td>
                        <td><strong>{p.name}</strong></td>
                        <td><span className="badge bg-primary-subtle text-primary">{p.uhid}</span></td>
                        <td>{p.phone}</td>
                        <td>
                          {p.bills.map((b, bi) => (
                            <span key={bi} className="badge bg-secondary me-1">{String(b).padStart(3, '0')}</span>
                          ))}
                        </td>
                        {medicineSaleSummary.isMulti && (
                          <td>
                            {(p.medicines || []).map((m, mi) => (
                              <span key={mi} className="badge bg-info-subtle text-info me-1 mb-1" style={{ fontSize: '11px' }}>{m}</span>
                            ))}
                          </td>
                        )}
                        <td className="text-center">
                          <span className="badge bg-success">{p.packageQty}</span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-warning text-dark">{p.looseQty}</span>
                        </td>
                        <td className="text-center">
                          <strong>{p.totalQty}</strong>
                        </td>
                        <td className="text-end">
                          <strong>₹{p.amount.toFixed(2)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light" style={{ position: 'sticky', bottom: 0 }}>
                    <tr>
                      <th colSpan={medicineSaleSummary.isMulti ? 6 : 5} className="text-end">Totals:</th>
                      <th className="text-center">{medicineSaleSummary.grandPackageQty}</th>
                      <th className="text-center">{medicineSaleSummary.grandLooseQty}</th>
                      <th className="text-center"><strong>{medicineSaleSummary.grandTotalQty}</strong></th>
                      <th className="text-end"><strong>₹{medicineSaleSummary.grandAmount.toFixed(2)}</strong></th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Bills Table */}
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Pharmacy Bills
              {(formData.selectedMedicines || []).map((m, i) => (
                <span key={i} className="badge bg-primary ms-1" style={{ fontSize: '11px' }}>
                  <i className="ri-capsule-line"></i> {m.label}
                </span>
              ))}
            </h5>
            <small className="text-muted"><i className="ri-information-line"></i> Click on any row to view medicines</small>
          </div>
          <div className="card-body">
            <DataTable
              columns={billColumns}
              data={filteredRecords}
              customStyles={customStyles}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              progressPending={loading}
              noHeader
              dense
              onRowClicked={(row) => viewMedicines(row)}
              pointerOnHover
            />
            <div className="text-end mt-3 text-muted">
              <strong>Cash:</strong> ₹{filteredPaymentSummary.cash.toFixed(2)} &nbsp;|&nbsp;
              <strong>Card:</strong> ₹{filteredPaymentSummary.card.toFixed(2)} &nbsp;|&nbsp;
              <strong>Online:</strong> ₹{filteredPaymentSummary.online.toFixed(2)} &nbsp;|&nbsp;
              <strong>Total Transactions:</strong> {filteredPayments.length}
            </div>
          </div>
        </div>

        {/* Payment Transactions Table */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Payment Transactions</h5>
            <button className="btn btn-sm btn-danger" onClick={exportPaymentTransactionsPDF}>
              <i className="ri-file-pdf-line"></i> Export PDF
            </button>
          </div>
          <div className="card-body">
            <DataTable
              columns={paymentColumns}
              data={filteredPayments}
              customStyles={customStyles}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              noHeader
              dense
              defaultSortFieldId={2}
              defaultSortAsc={false}
            />
            <div className="text-end mt-3 text-muted">
              <strong>Cash:</strong> ₹{filteredPaymentSummary.cash.toFixed(2)} &nbsp;|&nbsp;
              <strong>Card:</strong> ₹{filteredPaymentSummary.card.toFixed(2)} &nbsp;|&nbsp;
              <strong>Online:</strong> ₹{filteredPaymentSummary.online.toFixed(2)} &nbsp;|&nbsp;
              <strong>Total Transactions:</strong> {filteredPayments.length}
            </div>
          </div>
        </div>

        <DelBox />
        <NewOrder />
      </div>
    </div>
  );
}